<template>
  <overlay
    id="store-availability-drawer"
    key="storeAvailabilityDrawer"
    ref="storeAvailabilityDrawer"
    block-class="store-availability-drawer"
    direction="up-left"
    namespace="storeAvailabilityDrawer"
    type="shelf-drawer"
  >
    <template #title>
      <span
        class="store-availability-drawer__title store-availability-drawer__title--desktop"
        v-text="$string('product.store_availability.drawer_title')"
      />

      <span class="store-availability-drawer__title store-availability-drawer__title--mobile">
        <icon-left />

        <span v-text="$string('product.store_availability.results_title')" />
      </span>
    </template>

    <template #body>
      <div class="store-availability-drawer">
        <form
          v-if="showForm"
          class="store-availability-drawer__form"
        >
          <div class="form-group">
            <div class="form-group__field">
              <product-options
                class="store-availability-drawer__options"
                :drawer-color-swatch="activeSwatch"
                is-drawer
                hide-colors
                hide-labels
                :is-one-size="isOneSize"
                namespace="store-availability-drawer"
                :product="product"
                :section="section"
                show-size-mobile
                :variant="variant"
                @update-variant="handleUpdateVariant"
              />

              <p
                v-show="form.sizeError"
                :id="`store-availability-drawer-size-error-${section.id}`"
                v-text="form.sizeError"
              />
            </div>
          </div>

          <div class="form-group">
            <div class="form-group__field store-availability-drawer__zip-code">
              <label
                class="form-group__label"
                :for="`store-availability-drawer-zipcode-${section.id}`"
              >
                <span
                  class="text-body-m-regular"
                  v-text="$string('product.store_availability.form.zip_code_label')"
                />

                <abbr
                  :title="$string('product.store_availability.form.required_label')"
                  v-text="$string('product.store_availability.form.required_symbol')"
                />
              </label>

              <input
                :id="`store-availability-drawer-zipcode-${section.id}`"
                v-model="form.zipCode"
                :aria-describedby="getAriaDescribedby()"
                autocomplete="postal-code"
                :aria-invalid="getAriaInvalid()"
                class="form-group__input"
                :class="{[$class('error')]: state === 'error'}"
                name="zipcode"
                :placeholder="$string('product.store_availability.form.zip_code_placeholder')"
                required
                type="text"
              >

              <p
                v-show="form.zipCodeError"
                :id="`store-availability-drawer-zipcode-error-${section.id}`"
                v-text="form.zipCodeError"
              />
            </div>
          </div>

          <btn
            class="form-group"
            :label="$string('product.store_availability.form.submit')"
            :modifiers="ctaModifiers"
            type="submit"
            @click.prevent="handleZipCodeValidation"
          />
        </form>

        <div
          v-if="showResults"
          class="store-availability-drawer__stores"
        >
          <h2
            class="store-availability-drawer__stores-title text-body-m-regular"
            v-text="storesTitle"
          />

          <ul v-if="storeArray.length">
            <template
              v-for="store of storeArray"
              :key="store.id"
            >
              <li
                v-if="showStore(store)"
                class="store-availability-drawer__store text-body-m-regular"
                role="group"
                :aria-label="$string('product.store_availability.store_label')"
              >
                <address
                  class="store-availability-drawer__address"
                >
                  <span
                    class="store-availability-drawer__country"
                    v-text="getCountryName(store.countryCode)"
                  />

                  <span
                    v-if="section.enableDisplayNames"
                    v-html="formatAddress(store.name)"
                  />

                  <br v-if="section.enableDisplayNames">
                </address>

                <span v-text="stockMessage" />
              </li>
            </template>
          </ul>

          <p
            v-else
            v-text="$string('product.store_availability.no_results')"
          />
        </div>
      </div>
    </template>
  </overlay>
</template>

<script>

/**
 * Vue: Store availability drawer (store-availability-drawer)
 * -----------------------------------------------------------------------------
 * Drawer displaying product and variant store availability, powered by GeoStock
 * Inventory app.
 *
 * @param {Object} overlay - Overlay API.
 * @param {Object} product - Liquid formatted product.
 * @param {Object} section - Shopify section data.
 *
 */
import { mapState } from 'vuex'

import ProductOptions from '~async/product-form/product-options'
import Btn from '~global/btn/btn'
import Overlay from '~global/overlay/overlay'
import IconLeft from '~icons/directional-navigation/chevron/left.svg'

export default {
  name: 'StoreAvailabilityDrawer',

  components: {
    Btn,
    IconLeft,
    ProductOptions,
    Overlay,
  },

  props: {
    activeSwatch: {
      type: Object,
      default: () => ({}),
    },
    product: {
      type: Object,
      default: () => ({}),
    },
    section: {
      type: Object,
      default: () => ({
        enableDisplayNames: true,
        id: '',
      }),
    },
  },

  data() {
    return {
      form: {
        hasSubmitted: false,
        selectedSize: '',
        sizeError: '',
        zipCode: '',
        zipCodeError: '',
      },
      showResults: false,
      state: 'loading',
      storeArray: [],
      variant: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Return submit button modifiers.
     * @returns {Boolean}
     */
    ctaModifiers() {
      return this.isMobile ? 'primary, block, center' : 'primary'
    },

    /**
     * Compute if the product only has one size option.
     * @returns {Boolean}
     */
    isOneSize() {
      const productOptions = this.product?.options || this.liquid?.options

      return productOptions.some((option) => {
        return option.name === 'Size' && option.values.length === 1
      })
    },

    /**
     * Compute if the given zip code matches US specifications.
     * @returns {Boolean}
     */
    isZipCodeValid() {
      return (/(?<zipcode>^\d{5}$)|(?<zipcode>^\d{5}-\d{4}$)/).test(this.form.zipCode)
    },

    /**
     * Compute whether to display the form.
     * @returns {Boolean}
     */
    showForm() {
      if (!this.isMobile) {
        return true
      }

      return !this.showResults
    },

    /**
     * Return in/out/low stock message.
     * @returns {String}
     */
    stockMessage() {
      if (!this.variant) {
        return ''
      }

      const isHighStock = this.variant.inventory > this.$variable('settings.product.lowStockThreshold')

      if (isHighStock) {
        return this.$string('product.store_availability.stock_messages.high')
      }

      return this.variant.inventory <= 0
        ? this.$string('product.store_availability.stock_messages.out')
        : this.$string('product.store_availability.stock_messages.low')
    },

    /**
     * Compute stores title based on selected variant.
     * @returns {String}
     */
    storesTitle() {
      return this.$string('product.store_availability.stores_title')
        .replace('{{ size }}', this.form.selectedSize)
    },
  },

  watch: {

    /**
     * Watch variant to update selected size.
     */
    variant() {
      if (!this.variant) {
        return
      }

      this.form.selectedSize = this.variant.selectedOptions
        .find((option) => option.name === 'Size')
        .value
    },
  },

  mounted() {
    this.setInitialVariant()
    this.setEventListeners()
    this.setEventBusListeners()
    this.$refs.storeAvailabilityDrawer.addCallback(this.overlayCallback)
  },

  methods: {

    /**
     * Return the id of the error message to populate the `aria-describedby`
     * attribute.
     * - Set to `null` by default, and if no error message is present to prevent
     *   the attribute from appearing and pointing to a non-existant element.
     * @returns {String|null}
     */
    getAriaDescribedby() {
      if (!this.form.hasSubmitted) {
        return null
      }

      return this.isZipCodeValid
        ? null
        : `store-availability-drawer-zipcode-error-${this.section.id}`
    },

    /**
     * Set `aria-invalid` attribute.
     * - Set to `null` by default to prevent the attribute from appearing on
     *   the element (mirrors similar Liquid implementations).
     * or 'email'.
     * @returns {Boolean|null}
     */
    getAriaInvalid() {
      if (!this.form.hasSubmitted) {
        return null
      }

      return this.isZipCodeValid ? null : true
    },

    /**
     * Return translated country string.
     * @param {String} countryCode - Country ISO code.
     * @returns {String}
     */
    getCountryName(countryCode) {

      /**
       * Use Intl.DisplayNames API to translate country code to its full,
       * translated name.
       */
      const siteLanguage = new Intl.DisplayNames(
        [cnvs.store.language],
        { type: 'region' },
      )

      return siteLanguage?.of(countryCode)
        ? siteLanguage.of(countryCode)
        : countryCode
    },

    /**
     * Set event listeners to ensure that GeoStock data has loaded.
     */
    setEventListeners() {
      window.addEventListener('jtcVariantLocationsLoaded', () => {
        this.setStoreArray()
        this.state = 'ready'
      })
    },

    /**
     * Set canvas event bus listeners.
     */
    setEventBusListeners() {
      cnvs.EventBus.listen('update-variant', (variant) => {
        this.variant = variant
      })
    },

    /**
     * Set variant on page load.
     */
    setInitialVariant() {
      const params = new URLSearchParams(document.location.search.substring(1))
      const urlVariantId = Number(params.get('variant'))

      this.variant = urlVariantId
        ? this.product.variants.find((variant) => variant.id === urlVariantId)
        : false
    },

    /**
     * Set store array based on GeoStock app data.
     */
    setStoreArray() {
      const locations = window.jtcProductInventoryInfo?.data?.variants[this.variant.id]?.locations

      this.storeArray = locations
        ? locations.filter(location => location.enabled)
        : []
    },

    /**
     * Determine whether or not to render a store.
     * @param {Object} store
     * @returns {Boolean}
     */
    showStore(store) {
      return store.enabled && store.stock > 0
    },

    /**
     * Format address to wrap name in heading tag.
     * @param {String} storeName
     * @returns {String}
     */
    formatAddress(storeName) {
      const name = storeName.match(/<a .+>[\w\s.]+<\/a>/)
      return storeName.replace(name, `<h3>${name}</h3>`)
    },

    /**
     * Handle variant update based on size selection.
     * @param {Object} variant - Formatted variant object.
     */
    handleUpdateVariant(variant) {

      /**
       * Set variant immediately to prevent potential issues with the EventBus
       * not updating before a user validates their zip code.
       */
      this.variant = variant

      cnvs.EventBus.emit('update-variant', variant)
    },

    /**
     * Handle form submit.
     * - Check if a size has been selected and if the zip code entered is a
     *   valid US code.
     */
    handleZipCodeValidation() {
      this.form.hasSubmitted = true
      this.resetErrorMessages()
      this.state = 'ready'

      if (!this.variant) {
        this.form.sizeError = this.$string('product.store_availability.form.size_error')
        this.state = 'error'
      }

      if (!this.isZipCodeValid) {
        this.form.zipCodeError = this.$string('product.store_availability.form.zip_code_error')
        this.state = 'error'
      }

      if (this.state === 'error') {
        return
      }

      this.showResults = true
    },

    /**
     * Set callback for when the overlay closes.
     */
    overlayCallback() {
      this.resetForm()

      cnvs.EventBus.emit('store-availability-drawer-expanded', false)
    },

    /**
     * Reset error messages to default (blank string).
     */
    resetErrorMessages() {
      this.form.sizeError = ''
      this.form.zipCodeError = ''
    },

    /**
     * Reset form state.
     */
    resetForm() {
      this.resetErrorMessages()
      this.showResults = false
      this.state = 'ready'
      this.form.zipCode = ''
    },
  },
}
</script>

<style lang="scss">
@import './store-availability-drawer';
</style>
